import React from "react";
import { motion } from "framer-motion";
import {
  FaShieldAlt,
  FaUserSecret,
  FaLock,
  FaEye,
  FaHandshake,
} from "react-icons/fa";
import { CLINIC_GOOGLE_MAPS_LINK } from "../../utils/addressUtils";
import {
  HELPDESK_PHONE_DISPLAY,
  EMERGENCY_PHONE_DISPLAY,
  HELPDESK_PHONE_TEL,
  EMERGENCY_PHONE_TEL,
} from "../../utils/clinicContact";
import "./Privacy.css";

const Privacy = () => {
  const currentDate = new Date().toLocaleDateString("en-US", {
    year: "numeric",
    month: "long",
    day: "numeric",
  });

  return (
    <div className="privacy-page">
      {/* Hero Section */}
      <section className="privacy-hero">
        <div className="container">
          <motion.div
            className="privacy-hero-content"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <h1>Privacy Policy</h1>
            <p>Your privacy and data security are our top priorities</p>
            <div className="privacy-badge">
              <FaShieldAlt className="privacy-icon" />
              <span>Last Updated: {currentDate}</span>
            </div>
          </motion.div>
        </div>
      </section>

      <div className="container">
        {/* Introduction */}
        <section className="privacy-section">
          <motion.div
            className="section-header"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2>Our Commitment to Privacy</h2>
            <p>
              At SATYA HEART CLINIC, we understand the sensitive nature of
              medical information and are committed to protecting your privacy
              and maintaining the confidentiality of your health records.
            </p>
          </motion.div>
        </section>

        {/* Information We Collect */}
        <section className="privacy-section">
          <motion.div
            className="privacy-card"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <div className="card-header">
              <FaUserSecret className="card-icon" />
              <h3>Information We Collect</h3>
            </div>
            <div className="card-content">
              <h4>Personal Health Information</h4>
              <ul>
                <li>Name, contact details, and identification information</li>
                <li>Medical history and current health conditions</li>
                <li>Diagnostic test results and treatment plans</li>
                <li>Medication history and prescriptions</li>
                <li>Appointment schedules and visit records</li>
                <li>Insurance and billing information</li>
              </ul>

              <h4>Technical Information</h4>
              <ul>
                <li>Website usage data and cookies</li>
                <li>Device information and IP addresses</li>
                <li>Communication preferences and settings</li>
              </ul>
            </div>
          </motion.div>
        </section>

        {/* How We Use Information */}
        <section className="privacy-section">
          <motion.div
            className="privacy-card"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.1 }}
          >
            <div className="card-header">
              <FaEye className="card-icon" />
              <h3>How We Use Your Information</h3>
            </div>
            <div className="card-content">
              <h4>Primary Uses</h4>
              <ul>
                <li>Providing medical care and treatment</li>
                <li>Managing appointments and scheduling</li>
                <li>Processing payments and insurance claims</li>
                <li>Communicating with you about your care</li>
                <li>Improving our services and patient experience</li>
              </ul>

              <h4>Legal and Regulatory Requirements</h4>
              <ul>
                <li>Compliance with healthcare regulations</li>
                <li>Reporting to health authorities when required</li>
                <li>Responding to legal requests and subpoenas</li>
                <li>Maintaining medical records as required by law</li>
              </ul>
            </div>
          </motion.div>
        </section>

        {/* Information Sharing */}
        <section className="privacy-section">
          <motion.div
            className="privacy-card"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            <div className="card-header">
              <FaHandshake className="card-icon" />
              <h3>Information Sharing and Disclosure</h3>
            </div>
            <div className="card-content">
              <h4>We Do NOT Share Your Information With:</h4>
              <ul>
                <li>Third-party marketers or advertisers</li>
                <li>Unauthorized individuals or organizations</li>
                <li>Companies for commercial purposes</li>
              </ul>

              <h4>We May Share Information With:</h4>
              <ul>
                <li>Your designated healthcare providers</li>
                <li>Insurance companies for billing purposes</li>
                <li>Legal authorities when required by law</li>
                <li>Emergency responders in life-threatening situations</li>
                <li>Your authorized representatives (with written consent)</li>
              </ul>
            </div>
          </motion.div>
        </section>

        {/* Data Security */}
        <section className="privacy-section">
          <motion.div
            className="privacy-card"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.3 }}
          >
            <div className="card-header">
              <FaLock className="card-icon" />
              <h3>Data Security and Protection</h3>
            </div>
            <div className="card-content">
              <h4>Security Measures</h4>
              <ul>
                <li>Encryption of sensitive data in transit and at rest</li>
                <li>Secure access controls and authentication</li>
                <li>Regular security audits and updates</li>
                <li>Staff training on privacy and security</li>
                <li>Physical security measures for paper records</li>
              </ul>

              <h4>Data Retention</h4>
              <ul>
                <li>Medical records retained as required by law</li>
                <li>Secure disposal of outdated information</li>
                <li>Regular review and cleanup of data</li>
              </ul>
            </div>
          </motion.div>
        </section>

        {/* Your Rights */}
        <section className="privacy-section">
          <motion.div
            className="privacy-card"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.4 }}
          >
            <div className="card-header">
              <FaShieldAlt className="card-icon" />
              <h3>Your Privacy Rights</h3>
            </div>
            <div className="card-content">
              <h4>You Have the Right To:</h4>
              <ul>
                <li>Access and review your medical records</li>
                <li>Request corrections to your information</li>
                <li>Receive a copy of your health records</li>
                <li>Request restrictions on information sharing</li>
                <li>File a complaint about privacy violations</li>
                <li>Withdraw consent for certain uses</li>
              </ul>

              <h4>How to Exercise Your Rights</h4>
              <p>
                Contact our Privacy Officer at{" "}
                <a href="mailto:satya.s.nimmagadda@gmail.com">
                  satya.s.nimmagadda@gmail.com
                </a>{" "}
                or call us at{" "}
                <a href={`tel:${HELPDESK_PHONE_TEL}`}>{HELPDESK_PHONE_DISPLAY}</a>{" "}
                to exercise any of these rights.
              </p>
            </div>
          </motion.div>
        </section>

        {/* Contact Information */}
        <section className="privacy-section">
          <motion.div
            className="privacy-contact"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.5 }}
          >
            <h3>Questions About This Privacy Policy?</h3>
            <p>
              If you have any questions or concerns about our privacy practices,
              please don't hesitate to contact us:
            </p>
            <div className="contact-details">
              <div className="contact-item">
                <strong>Privacy Officer:</strong> Dr. Satya Sudhish Nimmagadda
              </div>
              <div className="contact-item">
                <strong>Email:</strong>{" "}
                <a href="mailto:satya.s.nimmagadda@gmail.com">
                  satya.s.nimmagadda@gmail.com
                </a>
              </div>
              <div className="contact-item">
                <strong>Helpdesk:</strong>{" "}
                <a href={`tel:${HELPDESK_PHONE_TEL}`}>{HELPDESK_PHONE_DISPLAY}</a>
              </div>
              <div className="contact-item">
                <strong>Emergency:</strong>{" "}
                <a href={`tel:${EMERGENCY_PHONE_TEL}`}>
                  {EMERGENCY_PHONE_DISPLAY}
                </a>
              </div>
              <div className="contact-item">
                <strong>Address:</strong>{" "}
                <a
                  href={CLINIC_GOOGLE_MAPS_LINK}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="address-link"
                  title="Click to open in Google Maps"
                >
                  Shop No-6, Shopping Complex, Vasanth Nagar, IDPL Staff
                  Cooperative Housing Society, Vasanth Nagar, Kukatpally,
                  Hyderabad, Telangana 500085
                </a>
              </div>
            </div>
          </motion.div>
        </section>
      </div>
    </div>
  );
};

export default Privacy;
